import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import {
  AlturaAsientoRosa,
  ProfundidadAsientoRosa,
  ReposabrazosRosa,
  RespaldoRosa,
  TiempoUsoRosa,
  TelefonoRosa,
  PantallaRosa,
  RatonRosa,
  TecladoRosa,
  EntradaSillaRosa,
  EntradaPantallaTelefonoRosa,
  EntradaRatonTecladoRosa,
  EvaluacionRosaInput,
  ResultadoRosa,
  calcularEvaluacionRosa,
} from '../lib/rosa';
import {
  createRosaAssessment,
  fetchRosaAssessments,
  RosaAssessmentRow,
} from '../lib/gestion-tecnica-rosa';
import { generateRosaReportPDF } from '../lib/gestion-tecnica-rosa-pdf';
import { fetchJobPositionsFull, JobPositionLegalFields } from '../lib/gestion-tecnica-constructor';
import {
  RosaAlturaAsientoDiagram,
  RosaProfundidadAsientoDiagram,
  RosaReposabrazosDiagram,
  RosaRespaldoDiagram,
  RosaTelefonoDiagram,
  RosaPantallaDiagram,
  RosaRatonDiagram,
  RosaTecladoDiagram,
} from '../components/rosa/RosaBodyDiagrams';

// Mismos estilos/paleta que RebaMethodologyPage.tsx (fondo oscuro, números
// principales en 34px) — copiados tal cual, sin modificar.
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const cardStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20,
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', color: '#e2e8f0', fontSize: 13,
};

// Descripción + diagrama de referencia por sección (ver src/components/rosa/RosaBodyDiagrams.tsx).
const fieldDescStyle: React.CSSProperties = {
  fontSize: 12.5, color: '#94a3b8', margin: '0 0 8px 0', lineHeight: 1.45,
};
const diagramBoxStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', background: '#0b1220', border: '1px solid #1e293b',
  borderRadius: 8, padding: 10, marginBottom: 12,
};

// Colores por nivel de actuación ROSA: solo 2 valores (a diferencia de REBA,
// que tiene 5 niveles numéricos) — verde si no requiere acción inmediata,
// rojo si la puntuación final es ≥5.
const NIVEL_ACCION_COLORES: Record<'sin_accion_inmediata' | 'accion_requerida', { bg: string; border: string; text: string }> = {
  sin_accion_inmediata: { bg: 'rgba(47,191,113,0.12)', border: 'rgba(47,191,113,0.3)', text: '#2FBF71' },
  accion_requerida: { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
};
const NIVEL_ACCION_LABEL: Record<'sin_accion_inmediata' | 'accion_requerida', string> = {
  sin_accion_inmediata: 'Sin acción inmediata requerida',
  accion_requerida: 'Acción requerida (puntuación ≥5)',
};

function ResultadoIntermedio({ label, valor }: { label: string; valor: number }) {
  return (
    <div>
      <span style={{ color: '#94a3b8', fontSize: 12 }}>{label}</span>
      <div style={{ fontSize: 20, fontWeight: 600, color: '#e2e8f0' }}>{valor}</div>
    </div>
  );
}

export default function RosaMethodologyPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';
  const isSuperAdmin         = profile?.role === 'superadmin';

  // ── Plan gate (after all hooks, mismo criterio que REBA) ──
  const planAllowed = isSuperAdmin || !displayCompany || hasPlanAccess(displayCompany.plan, 'metodologia-rosa');

  const [jobPositions, setJobPositions] = useState<JobPositionLegalFields[]>([]);
  const [savedAssessments, setSavedAssessments] = useState<RosaAssessmentRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [descripcion, setDescripcion] = useState('');
  const [selectedJobPositionId, setSelectedJobPositionId] = useState('');

  // ── Un solo objeto de estado por sección (Silla / Teléfono-Pantalla /
  // Ratón-Teclado) en vez de campos sueltos, para mantener el componente
  // manejable con 8 subcampos + booleanos por sección. ──
  const [silla, setSilla] = useState<EntradaSillaRosa>({
    alturaAsiento: 'rodillas_90', alturaNoRegulable: false, espacioInsuficienteBajoMesa: false,
    profundidadAsiento: 'neutra_8cm', profundidadNoRegulable: false,
    reposabrazos: 'neutro_90_relajado', reposabrazosDemasiadoAnchos: false, reposabrazosNoRegulables: false, reposabrazosBordesDurosOFilosos: false,
    respaldo: 'neutro_95_110', respaldoSuperficieAlta: false, respaldoNoRegulable: false,
    tiempoUso: 'entre_30min_1h_o_1_4h_dia',
  });

  const [pantallaTelefono, setPantallaTelefono] = useState<EntradaPantallaTelefonoRosa>({
    telefono: 'neutro_1mano_manosLibres', telefonoSujecionHombroCuello: false, telefonoSinManosLibres: false, telefonoTiempoUso: 'entre_30min_1h_o_1_4h_dia',
    pantalla: 'neutra_40_75cm_altura_ojos', pantallaDistanciaMayor75cm: false, pantallaGiroCuello: false, pantallaSinPortadocumentos: false, pantallaReflejos: false, pantallaTiempoUso: 'entre_30min_1h_o_1_4h_dia',
  });

  const [ratonTeclado, setRatonTeclado] = useState<EntradaRatonTecladoRosa>({
    raton: 'neutro_alineado_hombro', ratonPequenoAgarrePinza: false, ratonTecladoDiferentesAlturas: false, ratonReposamanosDuro: false, ratonTiempoUso: 'entre_30min_1h_o_1_4h_dia',
    teclado: 'neutro_muneca_recta', tecladoDesviacionAlEscribir: false, tecladoElevadoHombrosEncogidos: false, tecladoAlcancePorEncimaCabeza: false, tecladoSoporteNoAjustable: false, tecladoTiempoUso: 'entre_30min_1h_o_1_4h_dia',
  });

  const actualizarSilla = (cambios: Partial<EntradaSillaRosa>) => setSilla(prev => ({ ...prev, ...cambios }));
  const actualizarPantallaTelefono = (cambios: Partial<EntradaPantallaTelefonoRosa>) => setPantallaTelefono(prev => ({ ...prev, ...cambios }));
  const actualizarRatonTeclado = (cambios: Partial<EntradaRatonTecladoRosa>) => setRatonTeclado(prev => ({ ...prev, ...cambios }));

  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [ultimaEvaluacionGuardada, setUltimaEvaluacionGuardada] = useState<RosaAssessmentRow | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);

  const loadData = useCallback(async () => {
    if (!companyId) { setLoadingData(false); return; }
    setLoadingData(true);
    try {
      const [positions, assessments] = await Promise.all([
        fetchJobPositionsFull(companyId),
        fetchRosaAssessments(companyId),
      ]);
      setJobPositions(positions);
      setSavedAssessments(assessments);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos');
    } finally {
      setLoadingData(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ===== Cálculo en vivo (sin botón "Calcular") =====
  const evaluacionInput: EvaluacionRosaInput = { silla, pantallaTelefono, ratonTeclado };
  const resultado: ResultadoRosa = calcularEvaluacionRosa(evaluacionInput);
  const colores = NIVEL_ACCION_COLORES[resultado.nivelAccion];

  const puedeGuardar = descripcion.trim().length > 0;

  const handleGuardar = async () => {
    setError(null);
    if (!descripcion.trim()) { setError('Ingresa una descripción del local/puesto/tarea evaluada'); return; }

    setGuardando(true);
    setGuardadoOk(false);
    try {
      await createRosaAssessment({
        companyId,
        jobPositionId: selectedJobPositionId || undefined,
        descripcion,
        inputs: evaluacionInput,
        resultado,
        createdBy: profile?.id ?? null,
      });
      // createRosaAssessment() no retorna el registro insertado (mismo patrón
      // que createRebaAssessment()) — se vuelve a consultar para obtener el
      // registro recién creado con su id real, necesario para el PDF.
      const assessments = await fetchRosaAssessments(companyId);
      setSavedAssessments(assessments);
      setUltimaEvaluacionGuardada(assessments[0] ?? null);
      setGuardadoOk(true);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar la evaluación');
    } finally {
      setGuardando(false);
    }
  };

  const handleDescargarPdf = async () => {
    if (!ultimaEvaluacionGuardada) {
      setError('No hay una evaluación guardada para generar el PDF');
      return;
    }
    setDescargandoPdf(true);
    try {
      await generateRosaReportPDF(
        ultimaEvaluacionGuardada,
        companyId,
        displayCompany?.name ?? 'Empresa',
        profile?.id ?? null
      );
    } catch (e: any) {
      setError(e.message ?? 'Error al generar el PDF');
    } finally {
      setDescargandoPdf(false);
    }
  };

  if (!planAllowed) {
    return <UpgradeWall {...PLAN_FEATURES['metodologia-rosa']} />;
  }

  if (loadingData) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Puestos de Oficina — Método ROSA</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Rapid Office Strain Assessment. Fuente: Sonne, M., Villalta, D.L. &amp; Andrews, D.M. (2012). Development and evaluation of an office ergonomic risk checklist: the Rapid Office Strain Assessment (ROSA). Applied Ergonomics, 43(1), 98-108.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Descripción (local, puesto o tarea evaluada)</label>
        <input
          style={inputStyle}
          type="text"
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Ej. Recepcionista, Analista contable..."
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Puesto de trabajo (opcional)</label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Puesto de trabajo (opcional)" value={selectedJobPositionId}
          onChange={setSelectedJobPositionId} vacio="— Sin asociar —" opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))} />
      </div>

      {/* ===== SECCIÓN A: SILLA ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Sección A — Silla</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Altura del asiento</label>
          <p style={fieldDescStyle}>
            Relación entre la altura de la rodilla y la altura del asiento, y si el pie mantiene contacto con el suelo.
          </p>
          <div style={diagramBoxStyle}>
            <RosaAlturaAsientoDiagram rango={silla.alturaAsiento} />
          </div>
          <select style={inputStyle} value={silla.alturaAsiento} onChange={e => actualizarSilla({ alturaAsiento: e.target.value as AlturaAsientoRosa })}>
            <option value="rodillas_90" style={{ color: '#000' }}>Rodillas a 90°, pies apoyados</option>
            <option value="bajo_rodillas_menor_90" style={{ color: '#000' }}>Silla baja, rodillas por debajo de 90°</option>
            <option value="alto_rodillas_mayor_90" style={{ color: '#000' }}>Silla alta, rodillas por encima de 90°</option>
            <option value="sin_contacto_suelo" style={{ color: '#000' }}>Sin contacto con el suelo</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={silla.alturaNoRegulable}
                onChange={e => actualizarSilla({ alturaNoRegulable: e.target.checked })} />
              No regulable (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={silla.espacioInsuficienteBajoMesa}
                onChange={e => actualizarSilla({ espacioInsuficienteBajoMesa: e.target.checked })} />
              Espacio insuficiente bajo la mesa (+1)
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Profundidad del asiento</label>
          <p style={fieldDescStyle}>
            Espacio libre entre el borde delantero del asiento y la parte posterior de la rodilla.
          </p>
          <div style={diagramBoxStyle}>
            <RosaProfundidadAsientoDiagram rango={silla.profundidadAsiento} />
          </div>
          <select style={inputStyle} value={silla.profundidadAsiento} onChange={e => actualizarSilla({ profundidadAsiento: e.target.value as ProfundidadAsientoRosa })}>
            <option value="neutra_8cm" style={{ color: '#000' }}>Espacio neutro (~8cm)</option>
            <option value="menor_8cm" style={{ color: '#000' }}>Menos de 8cm (asiento muy profundo)</option>
            <option value="mayor_8cm" style={{ color: '#000' }}>Más de 8cm (asiento corto)</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={silla.profundidadNoRegulable}
                onChange={e => actualizarSilla({ profundidadNoRegulable: e.target.checked })} />
              No regulable (+1)
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Reposabrazos</label>
          <p style={fieldDescStyle}>
            Posición del codo y del hombro respecto al reposabrazos (o ausencia de este).
          </p>
          <div style={diagramBoxStyle}>
            <RosaReposabrazosDiagram rango={silla.reposabrazos} />
          </div>
          <select style={inputStyle} value={silla.reposabrazos} onChange={e => actualizarSilla({ reposabrazos: e.target.value as ReposabrazosRosa })}>
            <option value="neutro_90_relajado" style={{ color: '#000' }}>Codo a 90°, hombro relajado</option>
            <option value="desviacion_alto_o_bajo" style={{ color: '#000' }}>Alto o bajo</option>
            <option value="sin_reposabrazos" style={{ color: '#000' }}>Sin reposabrazos</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={silla.reposabrazosDemasiadoAnchos}
                onChange={e => actualizarSilla({ reposabrazosDemasiadoAnchos: e.target.checked })} />
              Demasiado anchos (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={silla.reposabrazosNoRegulables}
                onChange={e => actualizarSilla({ reposabrazosNoRegulables: e.target.checked })} />
              No regulables (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={silla.reposabrazosBordesDurosOFilosos}
                onChange={e => actualizarSilla({ reposabrazosBordesDurosOFilosos: e.target.checked })} />
              Bordes duros o filosos (+1)
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Respaldo</label>
          <p style={fieldDescStyle}>
            Inclinación del respaldo respecto al asiento y presencia/adecuación del apoyo lumbar.
          </p>
          <div style={diagramBoxStyle}>
            <RosaRespaldoDiagram rango={silla.respaldo} />
          </div>
          <select style={inputStyle} value={silla.respaldo} onChange={e => actualizarSilla({ respaldo: e.target.value as RespaldoRosa })}>
            <option value="neutro_95_110" style={{ color: '#000' }}>95°-110°, apoyo lumbar</option>
            <option value="no_se_utiliza" style={{ color: '#000' }}>No se utiliza</option>
            <option value="sin_apoyo_lumbar_o_inadecuado" style={{ color: '#000' }}>Sin apoyo lumbar o inadecuado</option>
            <option value="inclinacion_fuera_95_110" style={{ color: '#000' }}>Inclinación fuera de 95°-110°</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={silla.respaldoSuperficieAlta}
                onChange={e => actualizarSilla({ respaldoSuperficieAlta: e.target.checked })} />
              Superficie alta / hombros encogidos (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={silla.respaldoNoRegulable}
                onChange={e => actualizarSilla({ respaldoNoRegulable: e.target.checked })} />
              No regulable (+1)
            </label>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Tiempo de uso de la silla</label>
          <select style={inputStyle} value={silla.tiempoUso} onChange={e => actualizarSilla({ tiempoUso: e.target.value as TiempoUsoRosa })}>
            <option value="menos_30min_o_menos_1h_dia" style={{ color: '#000' }}>Menos de 30min o menos de 1h/día (-1)</option>
            <option value="entre_30min_1h_o_1_4h_dia" style={{ color: '#000' }}>Entre 30min-1h o 1-4h/día (0)</option>
            <option value="mas_1h_o_mas_4h_dia" style={{ color: '#000' }}>Más de 1h o más de 4h/día (+1)</option>
          </select>
        </div>

        <div style={{ marginTop: 16 }}>
          <ResultadoIntermedio label="Puntuación Silla" valor={resultado.puntuacionSilla} />
        </div>
      </div>

      {/* ===== SECCIÓN B: TELÉFONO Y PANTALLA ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Sección B — Teléfono y Pantalla</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Teléfono</label>
          <p style={fieldDescStyle}>
            Distancia y forma de sujeción del teléfono al usarlo.
          </p>
          <div style={diagramBoxStyle}>
            <RosaTelefonoDiagram rango={pantallaTelefono.telefono} sujecionHombroCuello={pantallaTelefono.telefonoSujecionHombroCuello} />
          </div>
          <select style={inputStyle} value={pantallaTelefono.telefono} onChange={e => actualizarPantallaTelefono({ telefono: e.target.value as TelefonoRosa })}>
            <option value="neutro_1mano_manosLibres" style={{ color: '#000' }}>Neutro (una mano o manos libres)</option>
            <option value="alejado_mayor_30cm" style={{ color: '#000' }}>Alejado (&gt;30cm)</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={pantallaTelefono.telefonoSujecionHombroCuello}
                onChange={e => actualizarPantallaTelefono({ telefonoSujecionHombroCuello: e.target.checked })} />
              Sujeción hombro/cuello (+2)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={pantallaTelefono.telefonoSinManosLibres}
                onChange={e => actualizarPantallaTelefono({ telefonoSinManosLibres: e.target.checked })} />
              Sin manos libres (+1)
            </label>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Tiempo de uso del teléfono</label>
            <select style={inputStyle} value={pantallaTelefono.telefonoTiempoUso} onChange={e => actualizarPantallaTelefono({ telefonoTiempoUso: e.target.value as TiempoUsoRosa })}>
              <option value="menos_30min_o_menos_1h_dia" style={{ color: '#000' }}>Menos de 30min o menos de 1h/día (-1)</option>
              <option value="entre_30min_1h_o_1_4h_dia" style={{ color: '#000' }}>Entre 30min-1h o 1-4h/día (0)</option>
              <option value="mas_1h_o_mas_4h_dia" style={{ color: '#000' }}>Más de 1h o más de 4h/día (+1)</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Pantalla</label>
          <p style={fieldDescStyle}>
            Altura de la pantalla respecto a la altura de los ojos y flexión/extensión de cuello resultante.
          </p>
          <div style={diagramBoxStyle}>
            <RosaPantallaDiagram rango={pantallaTelefono.pantalla} giroCuello={pantallaTelefono.pantallaGiroCuello} />
          </div>
          <select style={inputStyle} value={pantallaTelefono.pantalla} onChange={e => actualizarPantallaTelefono({ pantalla: e.target.value as PantallaRosa })}>
            <option value="neutra_40_75cm_altura_ojos" style={{ color: '#000' }}>Neutra, 40-75cm a la altura de los ojos</option>
            <option value="baja_menor_30grados" style={{ color: '#000' }}>Baja (&lt;30°)</option>
            <option value="alta_extension_cuello" style={{ color: '#000' }}>Alta, con extensión de cuello</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={pantallaTelefono.pantallaDistanciaMayor75cm}
                onChange={e => actualizarPantallaTelefono({ pantallaDistanciaMayor75cm: e.target.checked })} />
              Distancia &gt;75cm (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={pantallaTelefono.pantallaGiroCuello}
                onChange={e => actualizarPantallaTelefono({ pantallaGiroCuello: e.target.checked })} />
              Giro de cuello (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={pantallaTelefono.pantallaSinPortadocumentos}
                onChange={e => actualizarPantallaTelefono({ pantallaSinPortadocumentos: e.target.checked })} />
              Sin portadocumentos (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={pantallaTelefono.pantallaReflejos}
                onChange={e => actualizarPantallaTelefono({ pantallaReflejos: e.target.checked })} />
              Reflejos en pantalla (+1)
            </label>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Tiempo de uso de la pantalla</label>
            <select style={inputStyle} value={pantallaTelefono.pantallaTiempoUso} onChange={e => actualizarPantallaTelefono({ pantallaTiempoUso: e.target.value as TiempoUsoRosa })}>
              <option value="menos_30min_o_menos_1h_dia" style={{ color: '#000' }}>Menos de 30min o menos de 1h/día (-1)</option>
              <option value="entre_30min_1h_o_1_4h_dia" style={{ color: '#000' }}>Entre 30min-1h o 1-4h/día (0)</option>
              <option value="mas_1h_o_mas_4h_dia" style={{ color: '#000' }}>Más de 1h o más de 4h/día (+1)</option>
            </select>
          </div>
        </div>

        <div>
          <ResultadoIntermedio label="Puntuación Sección B" valor={resultado.puntuacionSeccionB} />
        </div>
      </div>

      {/* ===== SECCIÓN C: RATÓN Y TECLADO ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Sección C — Ratón y Teclado</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Ratón</label>
          <p style={fieldDescStyle}>
            Alineación del ratón respecto al hombro y forma de agarre.
          </p>
          <div style={diagramBoxStyle}>
            <RosaRatonDiagram rango={ratonTeclado.raton} />
          </div>
          <select style={inputStyle} value={ratonTeclado.raton} onChange={e => actualizarRatonTeclado({ raton: e.target.value as RatonRosa })}>
            <option value="neutro_alineado_hombro" style={{ color: '#000' }}>Neutro, alineado con el hombro</option>
            <option value="desviacion_no_alineado" style={{ color: '#000' }}>Desviación, no alineado</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={ratonTeclado.ratonPequenoAgarrePinza}
                onChange={e => actualizarRatonTeclado({ ratonPequenoAgarrePinza: e.target.checked })} />
              Agarre pequeño en pinza (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={ratonTeclado.ratonTecladoDiferentesAlturas}
                onChange={e => actualizarRatonTeclado({ ratonTecladoDiferentesAlturas: e.target.checked })} />
              Ratón/teclado a diferentes alturas (+2)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={ratonTeclado.ratonReposamanosDuro}
                onChange={e => actualizarRatonTeclado({ ratonReposamanosDuro: e.target.checked })} />
              Reposamanos duro (+1)
            </label>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Tiempo de uso del ratón</label>
            <select style={inputStyle} value={ratonTeclado.ratonTiempoUso} onChange={e => actualizarRatonTeclado({ ratonTiempoUso: e.target.value as TiempoUsoRosa })}>
              <option value="menos_30min_o_menos_1h_dia" style={{ color: '#000' }}>Menos de 30min o menos de 1h/día (-1)</option>
              <option value="entre_30min_1h_o_1_4h_dia" style={{ color: '#000' }}>Entre 30min-1h o 1-4h/día (0)</option>
              <option value="mas_1h_o_mas_4h_dia" style={{ color: '#000' }}>Más de 1h o más de 4h/día (+1)</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Teclado</label>
          <p style={fieldDescStyle}>
            Posición de la muñeca respecto al teclado y elevación de hombros al escribir.
          </p>
          <div style={diagramBoxStyle}>
            <RosaTecladoDiagram rango={ratonTeclado.teclado} />
          </div>
          <select style={inputStyle} value={ratonTeclado.teclado} onChange={e => actualizarRatonTeclado({ teclado: e.target.value as TecladoRosa })}>
            <option value="neutro_muneca_recta" style={{ color: '#000' }}>Neutro, muñeca recta</option>
            <option value="desviacion_extension_mayor_15" style={{ color: '#000' }}>Desviación, extensión &gt;15°</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={ratonTeclado.tecladoDesviacionAlEscribir}
                onChange={e => actualizarRatonTeclado({ tecladoDesviacionAlEscribir: e.target.checked })} />
              Desviación al escribir (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={ratonTeclado.tecladoElevadoHombrosEncogidos}
                onChange={e => actualizarRatonTeclado({ tecladoElevadoHombrosEncogidos: e.target.checked })} />
              Elevado/hombros encogidos (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={ratonTeclado.tecladoAlcancePorEncimaCabeza}
                onChange={e => actualizarRatonTeclado({ tecladoAlcancePorEncimaCabeza: e.target.checked })} />
              Alcance por encima de la cabeza (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={ratonTeclado.tecladoSoporteNoAjustable}
                onChange={e => actualizarRatonTeclado({ tecladoSoporteNoAjustable: e.target.checked })} />
              Soporte no ajustable (+1)
            </label>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Tiempo de uso del teclado</label>
            <select style={inputStyle} value={ratonTeclado.tecladoTiempoUso} onChange={e => actualizarRatonTeclado({ tecladoTiempoUso: e.target.value as TiempoUsoRosa })}>
              <option value="menos_30min_o_menos_1h_dia" style={{ color: '#000' }}>Menos de 30min o menos de 1h/día (-1)</option>
              <option value="entre_30min_1h_o_1_4h_dia" style={{ color: '#000' }}>Entre 30min-1h o 1-4h/día (0)</option>
              <option value="mas_1h_o_mas_4h_dia" style={{ color: '#000' }}>Más de 1h o más de 4h/día (+1)</option>
            </select>
          </div>
        </div>

        <div>
          <ResultadoIntermedio label="Puntuación Sección C" valor={resultado.puntuacionSeccionC} />
        </div>
      </div>

      {/* ===== Resultado ===== */}
      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
          <ResultadoIntermedio label="Puntuación Silla" valor={resultado.puntuacionSilla} />
          <ResultadoIntermedio label="Puntuación Sección B" valor={resultado.puntuacionSeccionB} />
          <ResultadoIntermedio label="Puntuación Sección C" valor={resultado.puntuacionSeccionC} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <ResultadoIntermedio label="Puntuación Pantalla/Periféricos" valor={resultado.puntuacionPantallaPerifericos} />
        </div>

        <span style={{ color: '#94a3b8', fontSize: 12 }}>Puntuación Final ROSA</span>
        <div style={{ fontSize: 34, fontWeight: 600, color: '#e2e8f0', marginBottom: 14 }}>{resultado.puntuacionFinal}</div>

        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
            background: colores.bg, border: `1px solid ${colores.border}`,
          }}
        >
          <strong style={{ color: colores.text, fontSize: 14 }}>{NIVEL_ACCION_LABEL[resultado.nivelAccion]}</strong>
        </div>
      </div>

      <button
        onClick={handleGuardar}
        disabled={guardando || !puedeGuardar}
        style={{
          background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
          padding: '8px 16px', fontWeight: 600, cursor: guardando || !puedeGuardar ? 'not-allowed' : 'pointer',
          opacity: guardando || !puedeGuardar ? 0.6 : 1,
        }}
      >
        {guardando ? 'Guardando...' : 'Guardar evaluación'}
      </button>

      {guardadoOk && (
        <div style={{ color: '#2FBF71', fontSize: 13, marginTop: 10 }}>✓ Evaluación guardada correctamente</div>
      )}

      {guardadoOk && ultimaEvaluacionGuardada && (
        <button
          onClick={handleDescargarPdf}
          disabled={descargandoPdf}
          style={{
            background: '#3b82f6', color: '#0f172a', border: 'none', borderRadius: 6,
            padding: '8px 16px', fontWeight: 600, cursor: descargandoPdf ? 'not-allowed' : 'pointer',
            opacity: descargandoPdf ? 0.6 : 1, marginTop: 10, marginLeft: 10,
          }}
        >
          {descargandoPdf ? 'Generando PDF...' : 'Descargar reporte PDF'}
        </button>
      )}

      {savedAssessments.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Evaluaciones guardadas</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Descripción</th>
                <th style={{ padding: '6px 8px' }}>Puntuación Final</th>
                <th style={{ padding: '6px 8px' }}>Nivel</th>
                <th style={{ padding: '6px 8px' }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {savedAssessments.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 8px' }}>{a.descripcion}</td>
                  <td style={{ padding: '6px 8px' }}>{a.resultado.puntuacionFinal}</td>
                  <td style={{ padding: '6px 8px' }}>{NIVEL_ACCION_LABEL[a.resultado.nivelAccion]}</td>
                  <td style={{ padding: '6px 8px' }}>{new Date(a.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
